import { apiClient } from '@/lib/api';
import { ENDPOINTS } from '@/lib/api/endpoints';
import { ORDEN_NATURAL } from '../types/dynamic-pricing.types';
import type {
  DynamicProduct,
  SortOrder,
  SuggestionSort,
  ProductsResponse,
  LinkState,
  DataState,
  ProductFilterCounts,
  CompetitorStore,
  CompetitorProduct,
  MissingCompetitorItem,
  ExternalDataSource,
  PriceAlert,
  DynamicPriceLog,
  DynamicPricingStatus,
  ScrapeResult,
  CreateProductInput,
  CreateCompetitorInput,
  LocalDelCatalogo,
  CreateCompetitorProductInput,
  CreateDataSourceInput,
  ScrapedCatalogJob,
  CatalogItemsResponse,
  ScrapedCatalogItem,
  CatalogScrapeProgress,
  ScraperType,
  InferenceResult,
  CorridaDeInferencia,
  SuggestionsResponse,
  InferenceStats,
  InferenceProgress,
  ProductGroupsResponse,
  ProductsWithoutOptionsResponse,
  BusquedaEnCatalogo,
  ImportCsvResult,
  RevisionDeCsv,
  ProductoGuardado,
  MonitorRun,
  MonitorRunsResponse,
  ResumenPrecios,
  MonitorRunsFilters,
  MonitorHealth,
  ScraperStatusResponse,
  PriceHistoryResponse,
  CompetitorHistoryResponse,
  PricePositionResponse,
  TopChangesResponse,
  MarketVolatilityResponse,
  MarginByCategoryResponse,
  StoreVolatilityResponse,
  CoverageByCategoryResponse,
  ScheduledTask,
  ScheduledTaskAgendaItem,
  CreateScheduledTaskInput,
} from '../types/dynamic-pricing.types';

/**
 * Los filtros de la lista de productos, tal cual se pidieron. L0 (vault 272, PD-05):
 * «Eliminar todo lo filtrado» manda exactamente estos, no una versión recortada.
 */
export type ProductsFilters = {
  search?: string;
  isActive?: string;
  sortByStore?: string;
  hasCalculatedPrice?: boolean;
  onlyWithCompetitorPrice?: boolean;
  onlyOnOffer?: boolean;
  onlyLowConfidence?: boolean;
  onlyWithError?: boolean;
  withoutCompetitor?: boolean;
  linkState?: LinkState;
  dataState?: DataState;
  category?: string;
  priceMin?: number;
  priceMax?: number;
  /** Ronda F de L4 (CAT-1): sólo los vinculados con esta tienda («Vinculados» de Catálogo). */
  linkedStoreId?: string;
};

/** Ronda L2-D (ítem 51): la corrida que «Todos» pidió para una tienda. */
export interface CorridaPedidaDeTodas {
  jobId: string;
  tienda: string;
  /** null = no se pudo pedir (`error` dice por qué). */
  runId: string | null;
  error?: string;
}

/** Lo que terminó de comparar «Todos», tienda por tienda. */
export interface ResumenDeTodas {
  tiendas: number;
  propuestas: number;
  automaticas: number;
  conError: number;
  detalle: {
    jobId: string;
    tienda: string;
    propuestas: number;
    automaticas: number;
    error?: string;
  }[];
}

export const dynamicPricingService = {
  // Products
  getProducts: async (params?: {
    search?: string;
    category?: string;
    isActive?: string;
    page?: number;
    limit?: number;
    sortByStore?: string;
    sortDir?: 'asc' | 'desc';
    sortBy?: string;
    hasCalculatedPrice?: boolean;
    onlyWithCompetitorPrice?: boolean;
    onlyOnOffer?: boolean;
    onlyLowConfidence?: boolean;
    onlyWithError?: boolean;
    withoutCompetitor?: boolean;
    linkState?: LinkState;
    dataState?: DataState;
    priceMin?: number;
    priceMax?: number;
    linkedStoreId?: string;
  }): Promise<ProductsResponse> => {
    const query = new URLSearchParams();
    if (params?.search) query.set('search', params.search);
    if (params?.category) query.set('category', params.category);
    if (params?.isActive) query.set('isActive', params.isActive);
    if (params?.page) query.set('page', String(params.page));
    if (params?.limit) query.set('limit', String(params.limit));
    if (params?.sortByStore) query.set('sortByStore', params.sortByStore);
    if (params?.sortDir) query.set('sortDir', params.sortDir);
    if (params?.sortBy) query.set('sortBy', params.sortBy);
    if (params?.hasCalculatedPrice) query.set('hasCalculatedPrice', 'true');
    if (params?.onlyWithCompetitorPrice) query.set('onlyWithCompetitorPrice', 'true');
    if (params?.onlyOnOffer) query.set('onlyOnOffer', 'true');
    if (params?.onlyLowConfidence) query.set('onlyLowConfidence', 'true');
    if (params?.onlyWithError) query.set('onlyWithError', 'true');
    if (params?.withoutCompetitor) query.set('withoutCompetitor', 'true');
    if (params?.linkState && params.linkState !== 'all') query.set('linkState', params.linkState);
    if (params?.dataState && params.dataState !== 'all') query.set('dataState', params.dataState);
    if (params?.priceMin != null) query.set('priceMin', String(params.priceMin));
    if (params?.priceMax != null) query.set('priceMax', String(params.priceMax));
    if (params?.linkedStoreId) query.set('linkedStoreId', params.linkedStoreId);
    const qs = query.toString();
    return apiClient.get(`${ENDPOINTS.DYNAMIC_PRICING_PRODUCTS}${qs ? `?${qs}` : ''}`);
  },

  /**
   * Conteos de los chips. Van en un pedido aparte del de la tabla porque no dependen
   * de la página ni del orden. Los filtros de vínculo y de dato SÍ viajan (T5-3):
   * los chips de «Otros» cuentan sobre lo ya filtrado, como la lista.
   */
  getProductFilterCounts: async (
    params?: Record<string, unknown>
  ): Promise<ProductFilterCounts> => {
    const query = new URLSearchParams();
    for (const [k, v] of Object.entries(params ?? {})) {
      if (k === 'page' || k === 'limit') continue;
      if (v === undefined || v === null || v === '' || v === false) continue;
      query.set(k, String(v));
    }
    const qs = query.toString();
    return apiClient.get(`${ENDPOINTS.DYNAMIC_PRICING_PRODUCT_FILTER_COUNTS}${qs ? `?${qs}` : ''}`);
  },
  createProduct: async (data: CreateProductInput): Promise<DynamicProduct> => {
    return apiClient.post(ENDPOINTS.DYNAMIC_PRICING_PRODUCTS, data);
  },
  updateProduct: async (
    id: string,
    data: Partial<CreateProductInput> & { sacarDeEstrategias?: boolean; motivo?: string }
  ): Promise<ProductoGuardado> => {
    return apiClient.put(ENDPOINTS.DYNAMIC_PRICING_PRODUCT_BY_ID(id), data);
  },
  // El borrado de productos se retiró en el servidor (3-nov, 410): se archivan con
  // `bulkDeactivateProducts` y se restauran con `bulkReactivateProducts`.
  /** ¿Hay una Demo Store de donde importar? (sólo la empresa de demostración la tiene). */
  demoStoreDisponible: async (): Promise<boolean> => {
    const r = await apiClient.get<{ available: boolean }>(
      '/api/dynamic-pricing/import-from-demo-store/availability'
    );
    return r?.available === true;
  },
  /** Trae los productos de la Demo Store como Mis productos. Upsert por SKU: se puede repetir. */
  importarDeDemoStore: async (): Promise<{
    created: number;
    updated: number;
    processed: number;
    skipped: number;
  }> => {
    return apiClient.post('/api/dynamic-pricing/import-from-demo-store', { limit: 5000 });
  },
  /** L4 (D-L0-1): dar de baja (`isActive = false`), conservando vínculos e historial. */
  bulkDeactivateProducts: async (ids: string[]): Promise<{ deactivated: number }> => {
    return apiClient.post('/api/dynamic-pricing/products/bulk-deactivate', { ids });
  },
  /** «Todo lo filtrado», con el mismo discriminador que el borrado (`mode`, nunca `all`). */
  bulkDeactivateAllProducts: async (
    filters: ProductsFilters,
    expectedCount: number
  ): Promise<{ deactivated: number }> => {
    return apiClient.post('/api/dynamic-pricing/products/bulk-deactivate', {
      mode: 'filtered',
      filters,
      expectedCount,
    });
  },
  /** Archivar en vez de borrar (2-nov): «Restaurar» los archivados marcados. */
  bulkReactivateProducts: async (ids: string[]): Promise<{ reactivated: number }> => {
    return apiClient.post('/api/dynamic-pricing/products/bulk-reactivate', { ids });
  },
  /** «Restaurar» todos los archivados filtrados, con el número confirmado. */
  bulkReactivateAllProducts: async (
    filters: ProductsFilters,
    expectedCount: number
  ): Promise<{ reactivated: number }> => {
    return apiClient.post('/api/dynamic-pricing/products/bulk-reactivate', {
      mode: 'filtered',
      filters,
      expectedCount,
    });
  },
  importProductsCsv: async (file: File): Promise<ImportCsvResult> => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post('/api/dynamic-pricing/products/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 300000,
    });
  },
  /** Revisar antes de confirmar (4B-datos T12): lee el archivo igual que el import y no escribe. */
  revisarImportCsv: async (file: File): Promise<RevisionDeCsv> => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post<RevisionDeCsv>('/api/dynamic-pricing/products/import/revisar', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 300000,
    });
  },
  /**
   * Plan 267: la plantilla .xlsx que arma el servidor. Por axiosInstance y no
   * por apiClient: apiClient devuelve `.data.data` y rompe los Blob.
   */
  bajarPlantillaDeProductos: async (): Promise<Blob> => {
    const { default: axiosInstance } = await import('@/lib/api/axios-instance');
    const response = await axiosInstance.get('/api/dynamic-pricing/products/import/plantilla', {
      responseType: 'blob',
    });
    return response.data;
  },
  calculateProduct: async (id: string): Promise<DynamicProduct> => {
    return apiClient.post(ENDPOINTS.DYNAMIC_PRICING_PRODUCT_CALCULATE(id));
  },

  // Competitors
  getCompetitors: async (): Promise<CompetitorStore[]> => {
    return apiClient.get(ENDPOINTS.DYNAMIC_PRICING_COMPETITORS);
  },
  createCompetitor: async (data: CreateCompetitorInput): Promise<CompetitorStore> => {
    return apiClient.post(ENDPOINTS.DYNAMIC_PRICING_COMPETITORS, data);
  },
  /**
   * Busca locales en el catálogo global para que el cliente elija a sus
   * competidores sin pegar URLs. Devuelve, por cada uno, si el cliente ya lo sigue.
   */
  buscarLocalesEnCatalogo: async (
    q: string,
    ciudad?: string,
    categoria?: string
  ): Promise<LocalDelCatalogo[]> => {
    const params = new URLSearchParams();
    if (q) params.set('q', q);
    if (ciudad) params.set('ciudad', ciudad);
    if (categoria) params.set('categoria', categoria);
    return apiClient.get(`${ENDPOINTS.LOCALES_BUSCAR}?${params.toString()}`);
  },
  /** Las ciudades presentes en el catálogo, para el filtro del buscador. */
  getCiudadesDelCatalogo: async (): Promise<string[]> => {
    return apiClient.get(ENDPOINTS.LOCALES_CIUDADES);
  },
  updateCompetitor: async (
    id: string,
    data: Partial<CreateCompetitorInput>
  ): Promise<CompetitorStore> => {
    return apiClient.put(ENDPOINTS.DYNAMIC_PRICING_COMPETITOR_BY_ID(id), data);
  },
  deleteCompetitor: async (id: string): Promise<void> => {
    return apiClient.delete(ENDPOINTS.DYNAMIC_PRICING_COMPETITOR_BY_ID(id));
  },
  /**
   * Prender o apagar varios locales de una vez.
   *
   * Es el último paso del alta de un cliente de comida: el descubrimiento de zona
   * registra a todos los que entregan ahí —255 en la primera prueba— y hay que
   * quedarse con los ~20 que de verdad compiten. Apagar no borra.
   *
   * El servidor omite el local propio aunque venga en la lista.
   */
  bulkSetCompetitorsActive: async (
    ids: string[],
    isActive: boolean
  ): Promise<{
    cambiados: number;
    propiosOmitidos: number;
    noEncontrados: number;
    /** Copias de tiendas de Prixia que la empresa no puede encender (las habilita la plataforma). */
    deLaPlataformaOmitidas?: number;
    mensaje?: string;
    /** Copias fusionadas en otra de la misma tienda: no se vuelven a encender. */
    fusionadasOmitidas?: number;
    mensajeFusionadas?: string;
    isActive: boolean;
  }> => {
    // La dirección va por ENDPOINTS y no escrita a mano: todas llevan el prefijo
    // `/api`, y escribirla suelta lo omitió — el navegador pegaba en
    // `localhost:4000/dynamic-pricing/...` y el servidor contestaba 404.
    return apiClient.patch(ENDPOINTS.DYNAMIC_PRICING_COMPETITORS_BULK_ACTIVE, { ids, isActive });
  },
  /**
   * Cuántos competidores puede seguir, y con qué frescura se leen a esa cantidad.
   *
   * La `explicacion` viene armada del servidor a propósito: el límite no es una
   * traba comercial sino la capacidad del recolector —unas 80 lecturas por día—, y
   * decir el número sin el motivo se lee como funcionalidad escondida.
   */
  getLimiteCompetidores: async (): Promise<{
    activos: number;
    limite: number;
    disponibles: number;
    lecturasPorDia: number;
    lleno: boolean;
    explicacion: string;
  }> => {
    return apiClient.get(ENDPOINTS.DYNAMIC_PRICING_COMPETITORS_LIMITE);
  },
  resetCompetitorStore: async (id: string): Promise<void> => {
    return apiClient.post(ENDPOINTS.DYNAMIC_PRICING_COMPETITOR_RESET(id));
  },
  getMissingItems: async (storeId: string): Promise<MissingCompetitorItem[]> => {
    return apiClient.get(ENDPOINTS.DYNAMIC_PRICING_COMPETITOR_MISSING_ITEMS(storeId));
  },
  toggleCompetitorMonitoring: async (
    id: string,
    data: { enabled: boolean }
  ): Promise<CompetitorStore> => {
    return apiClient.patch(ENDPOINTS.DYNAMIC_PRICING_COMPETITOR_MONITORING(id), data);
  },

  updateMonitoringConfig: async (
    id: string,
    config: { priceCheckIntervalHours: number; rescrapeIntervalDays: number }
  ): Promise<CompetitorStore> => {
    return apiClient.patch(ENDPOINTS.DYNAMIC_PRICING_COMPETITOR_MONITORING(id), config);
  },

  // Competitor Products
  getCompetitorProducts: async (productId: string): Promise<CompetitorProduct[]> => {
    return apiClient.get(ENDPOINTS.DYNAMIC_PRICING_PRODUCT_COMPETITORS(productId));
  },
  createCompetitorProduct: async (
    data: CreateCompetitorProductInput
  ): Promise<CompetitorProduct> => {
    return apiClient.post(ENDPOINTS.DYNAMIC_PRICING_COMPETITOR_PRODUCTS, data);
  },
  updateCompetitorProduct: async (
    id: string,
    data: Partial<CreateCompetitorProductInput>
  ): Promise<CompetitorProduct> => {
    return apiClient.put(ENDPOINTS.DYNAMIC_PRICING_COMPETITOR_PRODUCT_BY_ID(id), data);
  },
  deleteCompetitorProduct: async (id: string): Promise<void> => {
    return apiClient.delete(ENDPOINTS.DYNAMIC_PRICING_COMPETITOR_PRODUCT_BY_ID(id));
  },

  // Data Sources
  getDataSources: async (): Promise<ExternalDataSource[]> => {
    return apiClient.get(ENDPOINTS.DYNAMIC_PRICING_DATA_SOURCES);
  },
  createDataSource: async (data: CreateDataSourceInput): Promise<ExternalDataSource> => {
    return apiClient.post(ENDPOINTS.DYNAMIC_PRICING_DATA_SOURCES, data);
  },
  updateDataSource: async (
    id: string,
    data: Partial<CreateDataSourceInput>
  ): Promise<ExternalDataSource> => {
    return apiClient.put(ENDPOINTS.DYNAMIC_PRICING_DATA_SOURCE_BY_ID(id), data);
  },
  deleteDataSource: async (id: string): Promise<void> => {
    return apiClient.delete(ENDPOINTS.DYNAMIC_PRICING_DATA_SOURCE_BY_ID(id));
  },

  // Logs
  getLogs: async (limit?: number): Promise<DynamicPriceLog[]> => {
    const qs = limit ? `?limit=${limit}` : '';
    const res = await apiClient.get(`${ENDPOINTS.DYNAMIC_PRICING_LOGS}${qs}`);
    return res.logs ?? [];
  },

  getInferenceLogs: async (params?: {
    page?: number;
    limit?: number;
  }): Promise<{
    logs: any[];
    total: number;
    page: number;
    totalPages: number;
  }> => {
    const query = new URLSearchParams({ actionPrefix: 'INFERENCE_' });
    if (params?.page) query.set('page', String(params.page));
    if (params?.limit) query.set('limit', String(params.limit));
    const res = await apiClient.get(`${ENDPOINTS.DYNAMIC_PRICING_LOGS}?${query.toString()}`);
    return res;
  },

  // Status
  getStatus: async (): Promise<DynamicPricingStatus> => {
    return apiClient.get(ENDPOINTS.DYNAMIC_PRICING_STATUS);
  },

  // Manual operations
  scrapeAll: async (): Promise<ScrapeResult> => {
    return apiClient.post(ENDPOINTS.DYNAMIC_PRICING_SCRAPE);
  },
  calculateAll: async (): Promise<{ calculatedCount: number }> => {
    return apiClient.post(ENDPOINTS.DYNAMIC_PRICING_CALCULATE_ALL);
  },

  startOwnStoreScrape: async (
    url: string,
    scraperType: string = 'auto',
    proxyUrl?: string
  ): Promise<{ jobId: string; storeId: string; storeName: string }> => {
    return apiClient.post('/api/dynamic-pricing/own-store/start-scrape', {
      ...(url ? { url } : {}),
      scraperType,
      ...(proxyUrl ? { proxyUrl } : {}),
    });
  },

  // Manual price entry
  updateCompetitorProductPrice: async (id: string, price: number): Promise<CompetitorProduct> => {
    return apiClient.put(ENDPOINTS.DYNAMIC_PRICING_COMPETITOR_PRODUCT_PRICE(id), { price });
  },

  // ── Catalog Scraping ────────────────────────────────────────
  getScraperTypes: async (): Promise<ScraperType[]> => {
    return apiClient.get(ENDPOINTS.DYNAMIC_PRICING_CATALOG_SCRAPER_TYPES);
  },
  startCatalogScrape: async (
    storeId: string,
    scraperType: string = 'auto',
    proxyUrl?: string,
    cleanScrape: boolean = false
  ): Promise<{ jobId: string; queued?: boolean }> => {
    return apiClient.post(ENDPOINTS.DYNAMIC_PRICING_CATALOG_SCRAPE, {
      storeId,
      scraperType,
      cleanScrape,
      ...(proxyUrl ? { proxyUrl } : {}),
    });
  },
  getCatalogJobs: async (): Promise<ScrapedCatalogJob[]> => {
    return apiClient.get(ENDPOINTS.DYNAMIC_PRICING_CATALOG_JOBS);
  },
  getCatalogJobProgress: async (id: string): Promise<CatalogScrapeProgress> => {
    return apiClient.get(ENDPOINTS.DYNAMIC_PRICING_CATALOG_JOB_PROGRESS(id));
  },
  cancelCatalogScrape: async (id: string): Promise<{ cancelled: boolean }> => {
    return apiClient.post(ENDPOINTS.DYNAMIC_PRICING_CATALOG_JOB_CANCEL(id));
  },
  resumeCatalogScrape: async (id: string): Promise<{ jobId: string }> => {
    return apiClient.post(ENDPOINTS.DYNAMIC_PRICING_CATALOG_JOB_RESUME(id));
  },
  /** L4 (PD-38): exporta lo mismo que muestra la lista (los mismos filtros). */
  exportProductsExcel: async (
    filters: ProductsFilters & { sortBy?: string; sortDir?: 'asc' | 'desc' }
  ): Promise<Blob> => {
    const { default: axiosInstance } = await import('@/lib/api/axios-instance');
    const response = await axiosInstance.get(ENDPOINTS.DYNAMIC_PRICING_PRODUCTS_EXPORT, {
      responseType: 'blob',
      params: filters,
    });
    return response.data;
  },
  deleteCatalogJob: async (id: string): Promise<void> => {
    return apiClient.delete(ENDPOINTS.DYNAMIC_PRICING_CATALOG_JOB_DELETE(id));
  },
  getCatalogItems: async (params: {
    jobId?: string;
    status?: string;
    search?: string;
    category?: string;
    page?: number;
    limit?: number;
  }): Promise<CatalogItemsResponse> => {
    const query = new URLSearchParams();
    if (params.jobId) query.set('jobId', params.jobId);
    if (params.status) query.set('status', params.status);
    if (params.search) query.set('search', params.search);
    if (params.category) query.set('category', params.category);
    if (params.page) query.set('page', String(params.page));
    if (params.limit) query.set('limit', String(params.limit));
    const qs = query.toString();
    return apiClient.get(`${ENDPOINTS.DYNAMIC_PRICING_CATALOG_ITEMS}${qs ? `?${qs}` : ''}`);
  },
  matchCatalogItem: async (id: string, productId: string): Promise<ScrapedCatalogItem> => {
    return apiClient.put(ENDPOINTS.DYNAMIC_PRICING_CATALOG_ITEM_MATCH(id), { productId });
  },
  bulkMatchCatalogItems: async (
    matches: { catalogItemId: string; productId: string }[]
  ): Promise<{ matchedCount: number }> => {
    return apiClient.post(ENDPOINTS.DYNAMIC_PRICING_CATALOG_ITEMS_BULK_MATCH, { matches });
  },
  ignoreCatalogItem: async (id: string, ignored: boolean): Promise<ScrapedCatalogItem> => {
    return apiClient.put(ENDPOINTS.DYNAMIC_PRICING_CATALOG_ITEM_IGNORE(id), { ignored });
  },
  importCatalogItemsAsProducts: async (
    itemIds: string[],
    strategy = 'MATCH_LOWEST'
  ): Promise<{ created: number; skipped: number }> => {
    return apiClient.post(
      '/api/dynamic-pricing/catalog/items/import-as-products',
      {
        itemIds,
        strategy,
      },
      { timeout: 300000 }
    ); // 5 minutos — importar muchos productos tarda
  },

  importAllJobItemsAsProducts: async (
    jobId: string,
    strategy = 'MATCH_LOWEST',
    strategyConfig?: Record<string, any>,
    replaceAll = false
  ): Promise<{ created: number; skipped: number; total: number }> => {
    return apiClient.post(
      `/api/dynamic-pricing/catalog/jobs/${jobId}/import-all-as-products`,
      {
        strategy,
        ...(strategyConfig ? { strategyConfig } : {}),
        ...(replaceAll ? { replaceAll: true } : {}),
      },
      { timeout: 300000 }
    ); // 5 minutos — importar miles de productos tarda
  },

  // ── Inference / Auto-Match ───────────────────────────────────────────────
  /**
   * Corre la inferencia contra TODOS los competidores leídos, uno tras otro.
   *
   * Sin esto, comparar contra diez competidores eran diez clics. Con veinte, nadie
   * lo hace — y la pantalla se queda en "sin vincular" mostrando datos que sí están.
   *
   * Ronda L2-D (ítem 51): en retail el servidor encola una corrida por tienda y
   * responde enseguida con `corridas`; la pantalla pregunta por cada una
   * (`esperarCorridas`). Comida responde el resumen de siempre (motor congelado).
   */
  runInferenceAll: async (): Promise<{ corridas: CorridaPedidaDeTodas[] } | ResumenDeTodas> => {
    return apiClient.post(
      ENDPOINTS.DYNAMIC_PRICING_CATALOG_INFER_ALL,
      {},
      { timeout: 600000 } // comida corre adentro: veinte competidores tardan
    );
  },
  runInference: async (
    jobId: string,
    options?: { minConfidence?: number; replaceExisting?: boolean },
    signal?: AbortSignal
  ): Promise<InferenceResult> => {
    return apiClient.post(ENDPOINTS.DYNAMIC_PRICING_CATALOG_JOB_INFER(jobId), options ?? {}, {
      timeout: 600000, // 10 minutos — inferencia con miles de productos tarda
      signal,
    });
  },

  getJobSuggestions: async (
    jobId: string,
    params?: {
      status?: string;
      minConfidence?: number;
      maxConfidence?: number;
      page?: number;
      limit?: number;
      q?: string;
    }
  ): Promise<SuggestionsResponse> => {
    const query = new URLSearchParams();
    if (params?.status) query.set('status', params.status);
    if (params?.minConfidence != null) query.set('minConfidence', String(params.minConfidence));
    if (params?.maxConfidence != null) query.set('maxConfidence', String(params.maxConfidence));
    if (params?.page) query.set('page', String(params.page));
    if (params?.limit) query.set('limit', String(params.limit));
    if (params?.q?.trim()) query.set('q', params.q.trim());
    const qs = query.toString();
    return apiClient.get(
      `${ENDPOINTS.DYNAMIC_PRICING_CATALOG_JOB_SUGGESTIONS(jobId)}${qs ? `?${qs}` : ''}`
    );
  },

  getAllSuggestions: async (params?: {
    status?: string;
    minConfidence?: number;
    page?: number;
    limit?: number;
    q?: string;
  }): Promise<SuggestionsResponse> => {
    const query = new URLSearchParams();
    if (params?.status) query.set('status', params.status);
    if (params?.minConfidence != null) query.set('minConfidence', String(params.minConfidence));
    if (params?.page) query.set('page', String(params.page));
    if (params?.limit) query.set('limit', String(params.limit));
    if (params?.q?.trim()) query.set('q', params.q.trim());
    const qs = query.toString();
    return apiClient.get(`${ENDPOINTS.DYNAMIC_PRICING_CATALOG_SUGGESTIONS}${qs ? `?${qs}` : ''}`);
  },

  /** Los números del embudo de la pestaña Inferencia. */
  getInferenceStats: async (jobId: string): Promise<InferenceStats> => {
    return apiClient.get(ENDPOINTS.DYNAMIC_PRICING_CATALOG_JOB_INFERENCE_STATS(jobId));
  },

  /** Los productos propios con candidatos esperando, cada uno con los suyos. */
  getProductGroups: async (
    jobId: string,
    params?: {
      page?: number;
      limit?: number;
      status?: string;
      minConfidence?: number;
      maxConfidence?: number;
      q?: string;
      onlyMulti?: boolean;
      /** Los que YA tienen precio de la tienda, en vez de la cola de decisiones. */
      onlyLinked?: boolean;
      sort?: SuggestionSort;
      order?: SortOrder;
    }
  ): Promise<ProductGroupsResponse> => {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', String(params.page));
    if (params?.limit) query.set('limit', String(params.limit));
    if (params?.status) query.set('status', params.status);
    if (params?.minConfidence != null) query.set('minConfidence', String(params.minConfidence));
    if (params?.maxConfidence != null) query.set('maxConfidence', String(params.maxConfidence));
    if (params?.q?.trim()) query.set('q', params.q.trim());
    if (params?.onlyMulti) query.set('onlyMulti', 'true');
    if (params?.onlyLinked) query.set('onlyLinked', 'true');
    // El criterio por defecto no viaja: así el caso normal pide la misma URL de
    // siempre y el backend no toma el camino caro de resolver nombres y precios.
    if (params?.sort && params.sort !== 'confidence') query.set('sort', params.sort);
    // La dirección solo viaja cuando NO es la natural de ese criterio.
    if (params?.order && params.order !== ORDEN_NATURAL[params.sort ?? 'confidence'])
      query.set('order', params.order);
    const qs = query.toString();
    return apiClient.get(
      `${ENDPOINTS.DYNAMIC_PRICING_CATALOG_JOB_PRODUCT_GROUPS(jobId)}${qs ? `?${qs}` : ''}`
    );
  },

  /** Cómo va la inferencia. Se consulta mientras corre, cada pocos segundos. */
  getInferenceProgress: (jobId: string): Promise<InferenceProgress> => {
    return apiClient.get(ENDPOINTS.DYNAMIC_PRICING_CATALOG_JOB_INFERENCE_PROGRESS(jobId));
  },

  /** Los productos propios a los que el buscador no les encontró nada. */
  getProductsWithoutOptions: async (
    jobId: string,
    params?: { page?: number; limit?: number; q?: string }
  ): Promise<ProductsWithoutOptionsResponse> => {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', String(params.page));
    if (params?.limit) query.set('limit', String(params.limit));
    if (params?.q?.trim()) query.set('q', params.q.trim());
    const qs = query.toString();
    return apiClient.get(
      `${ENDPOINTS.DYNAMIC_PRICING_CATALOG_JOB_WITHOUT_OPTIONS(jobId)}${qs ? `?${qs}` : ''}`
    );
  },

  /**
   * C4b: busca a mano en el catálogo de la tienda (el que mira la pantalla), para
   * vincular un artículo con `productId` cuando ninguna opción del sistema es la buena.
   */
  buscarEnCatalogo: async (
    storeId: string,
    params: { productId: string; q: string; jobId?: string | null; page: number; limit: number }
  ): Promise<BusquedaEnCatalogo> => {
    const query = new URLSearchParams({
      productId: params.productId,
      page: String(params.page),
      limit: String(params.limit),
    });
    if (params.q.trim()) query.set('q', params.q.trim());
    if (params.jobId) query.set('jobId', params.jobId);
    return apiClient.get(
      `${ENDPOINTS.DYNAMIC_PRICING_CATALOG_STORE_BUSCAR_ARTICULOS(storeId)}?${query.toString()}`
    );
  },

  /**
   * `enLote`: la persona la vinculó con «Vincular los N seleccionados» (ronda L2-D,
   * ítem 49). Lo confirmado en lote se guarda pero no enseña al sistema.
   */
  acceptSuggestion: async (
    id: string,
    opciones?: { enLote?: boolean }
  ): Promise<{ accepted: boolean; siblingsRejected: number }> => {
    return apiClient.put(
      ENDPOINTS.DYNAMIC_PRICING_CATALOG_SUGGESTION_ACCEPT(id),
      opciones?.enLote ? { enLote: true } : {}
    );
  },

  /**
   * `motivo: 'NINGUNO_ES'` cuando la persona descarta todas las opciones de un producto;
   * `enLote` cuando las descarta con «Descartar los N seleccionados» (ronda L2-D).
   */
  rejectSuggestion: async (
    id: string,
    motivo?: 'NINGUNO_ES',
    opciones?: { enLote?: boolean }
  ): Promise<void> => {
    return apiClient.put(ENDPOINTS.DYNAMIC_PRICING_CATALOG_SUGGESTION_REJECT(id), {
      ...(motivo ? { motivo } : {}),
      ...(opciones?.enLote ? { enLote: true } : {}),
    });
  },

  /** L2: pide la corrida en segundo plano. `runId: null` = empresa de comida (corrida de siempre). */
  startInferenceRun: async (
    jobId: string
  ): Promise<{ runId: string | null; yaEnCurso?: boolean; rubro?: 'COMIDA' }> => {
    return apiClient.post(ENDPOINTS.DYNAMIC_PRICING_INFERENCE_RUNS(jobId), {});
  },
  getInferenceRun: async (runId: string, signal?: AbortSignal): Promise<CorridaDeInferencia> => {
    return apiClient.get(ENDPOINTS.DYNAMIC_PRICING_INFERENCE_RUN(runId), { signal });
  },
  /** Ronda L2-E (ítem 1): la corrida en curso de la tienda del catálogo, o null. */
  getActiveInferenceRun: async (jobId: string): Promise<CorridaDeInferencia | null> => {
    return apiClient.get(ENDPOINTS.DYNAMIC_PRICING_INFERENCE_RUN_ACTIVE(jobId));
  },

  cleanInference: async (
    jobId: string
  ): Promise<{ deletedSuggestions: number; unlinkedItems: number; deletedCompetitors: number }> => {
    return apiClient.delete(ENDPOINTS.DYNAMIC_PRICING_CATALOG_JOB_CLEAN_INFERENCE(jobId));
  },

  cleanStoreInference: async (
    storeId: string
  ): Promise<{
    deletedSuggestions: number;
    unlinkedItems: number;
    deletedCompetitors: number;
    jobsProcessed: number;
  }> => {
    return apiClient.delete(ENDPOINTS.DYNAMIC_PRICING_CATALOG_STORE_CLEAN_INFERENCE(storeId));
  },

  fixJobPrices: async (jobId: string): Promise<void> => {
    await apiClient.post(ENDPOINTS.DYNAMIC_PRICING_CATALOG_JOB_FIX_PRICES(jobId), {});
  },

  // ── Monitor Runs (Historial de monitoreo) ─────────────────────
  getImportPreview: async (
    jobId: string,
    replaceAll = false
  ): Promise<{ total: number; willCreate: number; willLink: number }> => {
    return apiClient.get(
      `/api/dynamic-pricing/catalog/jobs/${jobId}/import-preview${replaceAll ? '?replaceAll=true' : ''}`
    );
  },

  getMonitorRuns: async (params?: MonitorRunsFilters): Promise<MonitorRunsResponse> => {
    const query = new URLSearchParams();
    if (params?.storeId) query.set('storeId', params.storeId);
    if (params?.type) query.set('type', params.type);
    if (params?.trigger) query.set('trigger', params.trigger);
    if (params?.scheduledTaskId) query.set('scheduledTaskId', params.scheduledTaskId);
    if (params?.from) query.set('from', params.from);
    if (params?.to) query.set('to', params.to);
    if (params?.conCambios) query.set('conCambios', 'true');
    if (params?.page) query.set('page', String(params.page));
    if (params?.limit) query.set('limit', String(params.limit));
    const qs = query.toString();
    return apiClient.get(`/api/dynamic-pricing/monitor/runs${qs ? `?${qs}` : ''}`);
  },

  getMonitorHealth: async (): Promise<MonitorHealth> => {
    return apiClient.get('/api/dynamic-pricing/monitor/health');
  },

  /** L4 (PD-37): el Resumen armado en el servidor, con la ventana de Monitoreo. */
  getResumen: async (): Promise<ResumenPrecios> => {
    return apiClient.get('/api/dynamic-pricing/resumen');
  },

  getScraperStatus: async (days = 90): Promise<ScraperStatusResponse> => {
    return apiClient.get(`/api/dynamic-pricing/monitor/scraper-status?days=${days}`);
  },

  getMonitorRun: async (id: string): Promise<MonitorRun> => {
    return apiClient.get(`/api/dynamic-pricing/monitor/runs/${id}`);
  },

  getMonitorRunAlerts: async (id: string): Promise<PriceAlert[]> => {
    return apiClient.get(`/api/dynamic-pricing/monitor/runs/${id}/alerts`);
  },

  cancelMonitorRun: async (id: string): Promise<MonitorRun> => {
    return apiClient.post(`/api/dynamic-pricing/monitor/runs/${id}/cancel`, {});
  },

  triggerMonitorRun: async (
    storeId?: string,
    parallel?: boolean,
    /** Sólo los productos vinculados (Monitoreo, 25-oct): minutos en vez de horas. */
    soloVinculados?: boolean
  ): Promise<{
    queued: boolean;
    storeId: string | null;
    message: string;
    /** «Todas»: las que no se pidieron por el máximo de lecturas nuevas del día. */
    limited?: string[];
    /** «Todas»: las que Prixia no está leyendo (pausadas por la plataforma). */
    skipped?: string[];
  }> => {
    const body: { storeId?: string; parallel?: boolean; soloVinculados?: boolean } = {};
    if (storeId) body.storeId = storeId;
    if (soloVinculados) body.soloVinculados = true;
    // El modo paralelo solo aplica a "todas las tiendas" (sin storeId).
    if (!storeId && parallel) body.parallel = true;
    return apiClient.post('/api/dynamic-pricing/monitor/runs/trigger', body);
  },

  // ========================================
  // ANALYTICS — Gráficos / Dashboard
  // ========================================

  getPriceHistory: async (
    productId: string,
    params?: { days?: number; storeId?: string }
  ): Promise<PriceHistoryResponse> => {
    const q = new URLSearchParams();
    if (params?.days) q.set('days', String(params.days));
    if (params?.storeId) q.set('storeId', params.storeId);
    const qs = q.toString();
    return apiClient.get(
      `${ENDPOINTS.DYNAMIC_PRICING_PRICE_HISTORY(productId)}${qs ? `?${qs}` : ''}`
    );
  },

  getCompetitorHistory: async (
    productId: string,
    params?: { days?: number; storeId?: string }
  ): Promise<CompetitorHistoryResponse> => {
    const q = new URLSearchParams();
    if (params?.days) q.set('days', String(params.days));
    if (params?.storeId) q.set('storeId', params.storeId);
    const qs = q.toString();
    return apiClient.get(
      `${ENDPOINTS.DYNAMIC_PRICING_COMPETITOR_HISTORY(productId)}${qs ? `?${qs}` : ''}`
    );
  },

  getPricePosition: async (params?: {
    category?: string;
    storeId?: string;
  }): Promise<PricePositionResponse> => {
    const q = new URLSearchParams();
    if (params?.category) q.set('category', params.category);
    if (params?.storeId) q.set('storeId', params.storeId);
    const qs = q.toString();
    return apiClient.get(
      `${ENDPOINTS.DYNAMIC_PRICING_ANALYTICS_PRICE_POSITION}${qs ? `?${qs}` : ''}`
    );
  },

  getTopChanges: async (params?: {
    days?: number;
    from?: string;
    to?: string;
    storeId?: string;
    category?: string;
    limit?: number;
    onlyLinked?: boolean;
    minPct?: number;
    minBs?: number;
    combine?: 'and' | 'or';
    types?: string;
  }): Promise<TopChangesResponse> => {
    const q = new URLSearchParams();
    if (params?.days) q.set('days', String(params.days));
    if (params?.from) q.set('from', params.from);
    if (params?.to) q.set('to', params.to);
    if (params?.storeId) q.set('storeId', params.storeId);
    if (params?.category) q.set('category', params.category);
    if (params?.limit) q.set('limit', String(params.limit));
    if (params?.onlyLinked) q.set('onlyLinked', 'true');
    if (params?.minPct != null) q.set('minPct', String(params.minPct));
    if (params?.minBs != null) q.set('minBs', String(params.minBs));
    if (params?.combine) q.set('combine', params.combine);
    if (params?.types) q.set('types', params.types);
    const qs = q.toString();
    return apiClient.get(`${ENDPOINTS.DYNAMIC_PRICING_ANALYTICS_TOP_CHANGES}${qs ? `?${qs}` : ''}`);
  },

  getMarketVolatility: async (params?: {
    days?: number;
    storeId?: string;
    from?: string;
    to?: string;
    minPct?: number;
    minBs?: number;
    combine?: 'and' | 'or';
    types?: string;
  }): Promise<MarketVolatilityResponse> => {
    const q = new URLSearchParams();
    if (params?.from && params?.to) {
      q.set('from', params.from);
      q.set('to', params.to);
    } else if (params?.days) {
      q.set('days', String(params.days));
    }
    if (params?.storeId) q.set('storeId', params.storeId);
    if (params?.minPct != null) q.set('minPct', String(params.minPct));
    if (params?.minBs != null) q.set('minBs', String(params.minBs));
    if (params?.combine) q.set('combine', params.combine);
    if (params?.types) q.set('types', params.types);
    const qs = q.toString();
    return apiClient.get(
      `${ENDPOINTS.DYNAMIC_PRICING_ANALYTICS_MARKET_VOLATILITY}${qs ? `?${qs}` : ''}`
    );
  },

  getMarginByCategory: async (): Promise<MarginByCategoryResponse> => {
    return apiClient.get(ENDPOINTS.DYNAMIC_PRICING_ANALYTICS_MARGIN_BY_CATEGORY);
  },

  getStoreVolatility: async (params?: {
    days?: number;
    from?: string;
    to?: string;
    minPct?: number;
    minBs?: number;
    combine?: 'and' | 'or';
    types?: string;
  }): Promise<StoreVolatilityResponse> => {
    const q = new URLSearchParams();
    if (params?.from && params?.to) {
      q.set('from', params.from);
      q.set('to', params.to);
    } else if (params?.days) {
      q.set('days', String(params.days));
    }
    if (params?.minPct != null) q.set('minPct', String(params.minPct));
    if (params?.minBs != null) q.set('minBs', String(params.minBs));
    if (params?.combine) q.set('combine', params.combine);
    if (params?.types) q.set('types', params.types);
    const qs = q.toString();
    return apiClient.get(
      `${ENDPOINTS.DYNAMIC_PRICING_ANALYTICS_STORE_VOLATILITY}${qs ? `?${qs}` : ''}`
    );
  },

  getCoverageByCategory: async (): Promise<CoverageByCategoryResponse> => {
    return apiClient.get(ENDPOINTS.DYNAMIC_PRICING_ANALYTICS_COVERAGE_BY_CATEGORY);
  },

  // ── Scheduled Tasks (Calendario de scraping) ──────────────────
  getScheduledTasks: async (): Promise<ScheduledTask[]> => {
    return apiClient.get('/api/dynamic-pricing/scheduled-tasks');
  },
  getScheduledAgenda: async (days = 14): Promise<ScheduledTaskAgendaItem[]> => {
    return apiClient.get(`/api/dynamic-pricing/scheduled-tasks/agenda?days=${days}`);
  },
  createScheduledTask: async (data: CreateScheduledTaskInput): Promise<ScheduledTask> => {
    return apiClient.post('/api/dynamic-pricing/scheduled-tasks', data);
  },
  updateScheduledTask: async (
    id: string,
    data: Partial<CreateScheduledTaskInput>
  ): Promise<ScheduledTask> => {
    return apiClient.patch(`/api/dynamic-pricing/scheduled-tasks/${id}`, data);
  },
  deleteScheduledTask: async (id: string): Promise<void> => {
    return apiClient.delete(`/api/dynamic-pricing/scheduled-tasks/${id}`);
  },
  runScheduledTaskNow: async (id: string): Promise<void> => {
    return apiClient.post(`/api/dynamic-pricing/scheduled-tasks/${id}/run-now`, {});
  },
};
